import config from "@/config"
import { foxentryPhoneValidation } from "@/services"
import { t } from "@/i18n-instance"
import { debounce } from "lodash"

export const setSubmitButton = ({ 
  submitButton = null, 
  loading = false, 
  disabled = false 
}) => {
  if (!submitButton) {
    return
  }
  if (loading) {
    submitButton.classList.add('b-button--loading')
  } else {
    submitButton.classList.remove('b-button--loading')
  }

  submitButton.disabled = Boolean(disabled)
}

export const setGroupValidationState = ({ group, isValid, message }) => {
  const helperText = group.querySelector('.b-form__helper-text')

  if (helperText) {
    helperText.textContent = message
    helperText.classList[!isValid ? 'remove' : 'add']('d-none')
  }
  
  group.classList[isValid ? 'remove' : 'add']('b-form__group--error');
}

const validatePhone = async ({ prefix, number, group, submitButton, invalidFieldIds, invalidAcceptances }) => {
  if (prefix && number) {
    setSubmitButton({ submitButton, loading: true, disabled: true })
    const { data } = await foxentryPhoneValidation(
      { prefix, number },
      { validationType: 'extended', formatNumber: false }
    );

    const isValid = data.response.result.isValid;

    setGroupValidationState({ group, isValid, message: t('validations.phoneInvalid') });
    
    // Check if there are any other invalid fields
    const isFormValid = isValid && invalidFieldIds.length === 0 && invalidAcceptances.length === 0;

    // Only enable the submit button if the phone is valid and there are no other invalid fields
    setSubmitButton({ submitButton, loading: false, disabled: !isFormValid });
  }
};


export const handlePhoneSelect = ({ group, input, submitButton, invalidFieldIds, invalidAcceptances }) => {
  let prefix = config.phonePrefix;
  let number;

  const select = group.querySelector('.b-form-select--phone select');
  const hiddenInput = group.querySelector('input[type="hidden"]');

  if (select && hiddenInput) {
    const validatePhoneDebounced = debounce(validatePhone, 300);

    select.addEventListener('change', async (event) => {
      prefix = event.target.value;
      hiddenInput.value = `${prefix ?? ''} ${number ?? ''}`;
      validatePhoneDebounced({ prefix, number, group, submitButton, invalidFieldIds, invalidAcceptances });
    });

    input.addEventListener('input', async (event) => {
      number = event.target.value;
      hiddenInput.value = `${prefix ?? ''} ${number ?? ''}`;
      validatePhoneDebounced({ prefix, number, group, submitButton, invalidFieldIds, invalidAcceptances });
    });
  }
};


export const handleLocaleSelect = ({ group, type }) => {
  const select = group.querySelector(`.b-form-select--${type} select`)
  const hiddenInput = group.querySelector('input[type="hidden"]')

  if (select && hiddenInput) {
    hiddenInput.value = config.language.toUpperCase()

    select.addEventListener('change', (event) => {
      hiddenInput.value = event.target.value.toUpperCase()
    })
  }
}

const getGroupValid = ({ group, event }) => {
  const validationPattern = group
    .getAttribute('data-validation')
    .match(/^\/(.*)\/([a-z]*)$/)

  if (!validationPattern) {
    return true
  }

  const [_, pattern, flags] = validationPattern
  const regex = new RegExp(pattern, flags);

  const field = event.target
  const isFieldEmpty = field.value === ''
  const isFieldValid = !isFieldEmpty && regex.test(field.value.trim())

  return { isFieldValid, isFieldEmpty }
}

export const handleValidationEvent = ({ event, submitButton, invalidFieldIds, invalidAcceptances }) => {
  const isValidatingCheckbox = event.target.getAttribute('type') === 'checkbox'

  const groupToValidate = (() => {
    if(isValidatingCheckbox) {
      return event.target.closest('.b-form__group')
    }
    return event.target.closest('.b-form__group[data-validation][data-error]')
  })()

  if (!groupToValidate) return

  const groupValidation = isValidatingCheckbox 
    ? event.target.checked 
    : getGroupValid({ group: groupToValidate, event })

  const isValid = isValidatingCheckbox 
    ? groupValidation 
    : groupValidation.isFieldValid && !groupValidation.isFieldEmpty
  
  setGroupValidationState({
    group: groupToValidate,
    isValid,
    message: groupValidation?.isFieldEmpty
      ? t('validations.required')
      : t(`validations.${groupToValidate?.getAttribute('data-error') ?? 'required'}`)
  })

  const fieldID = event.target?.id || event.target?.name

  if (!isValid) {
    if (!invalidFieldIds.includes(fieldID)) {
      invalidFieldIds.push(fieldID)
    }
  } else {
    const index = invalidFieldIds.indexOf(fieldID);
    if (index > -1) {
      invalidFieldIds.splice(index, 1);
    }
  }

  const isFormValid = invalidFieldIds.length === 0

  setSubmitButton({ submitButton, disabled: !isFormValid })
}